import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { useAppStore } from '@/store/appStore';

/**
 * Route prefix for a specific chat conversation (`/ui/chat/:executionId`).
 * Mirrors the `startsWith('/ui/chat/')` matcher used for the "Chats" nav item.
 */
const CHAT_CONVERSATION_PREFIX = '/ui/chat/';

/**
 * Auto-closes the sidebar rail flyouts (chat history, My Workforce) on
 * navigation.
 *
 * The flyouts are fixed, full-height overlays rendered by `AppLayout`, but
 * their open state lives in the global store. Because `AppLayout` is mounted
 * per-page (there is no persistent layout), nothing closed them on navigation
 * — so clicking a nav tab left an overlay covering the new page.
 *
 * - My Workforce always closes (picking an agent routes to its page).
 * - Chat history stays open when moving between conversations
 *   (`/ui/chat/:id`) so users can keep browsing, matching the chat sidebar.
 *
 * Must be mounted exactly once inside the router (it relies on `useLocation`).
 */
export function useCloseSidebarPanelsOnNavigate(): void {
  const { pathname } = useLocation();
  const closeChatHistory = useAppStore((state) => state.closeChatHistory);
  const closeWorkforce = useAppStore((state) => state.closeWorkforce);
  // Track the previous path so we only react to actual navigation, not to the
  // initial mount or to a panel being opened in place (which is the same path).
  const previousPathnameRef = useRef(pathname);

  useEffect(() => {
    if (previousPathnameRef.current === pathname) return;
    previousPathnameRef.current = pathname;

    closeWorkforce();

    if (!pathname.startsWith(CHAT_CONVERSATION_PREFIX)) {
      closeChatHistory();
    }
  }, [pathname, closeChatHistory, closeWorkforce]);
}
